"use client";

import {
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from "react";

import { ArrowUpRight, ChevronDown } from "lucide-react";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "../../shadcn/ui/dropdown-menu";

import Link from "next/link";
import { PageNavDrawerMenu } from "./PageNavDrawerMenu";
import { findActiveNav } from "./findActiveNav";
import { matchesPath } from "./matchesPath";
import { cn } from "@punchup/utils/cn";
import { useMediaQuery } from "@punchup/utils/hooks";
import { usePathname } from "next/navigation";
import { withOpacity } from "@punchup/utils/colors";

interface NavCSSProperties extends React.CSSProperties {
  "--nav-color": string;
  "--nav-active-color": string;
  "--nav-border-color": string;
}

interface DropdownCSSProperties extends React.CSSProperties {
  "--dropdown-accent": string;
  "--dropdown-accent-hover-bg": string;
  "--dropdown-accent-active-bg": string;
}

export interface PageNavLink {
  label: string;
  href: string;
  // Rendered before the label, inside the same element, so it inherits the
  // nav's color, size, and active-state treatment.
  icon?: React.ReactNode;
  type?: "internal" | "external";
  title?: string;
  eventName?: string;
  ctaSource?: string;
  // Unique identity used when href isn't a stable key - e.g. a dropdown
  // group whose href is empty. Falls back to href when undefined.
  id?: string;
  // When present, this link renders as a dropdown trigger whose menu lists
  // the children. The trigger itself doesn't navigate.
  children?: PageNavLink[];
}

const linkKey = (link: PageNavLink): string => link.id ?? link.href;

// The label box inside a tab. Keyboard focus on the tab draws the brand ring
// around this box, not the tab: the margins keep the ring above the row's
// underline and inside the tab, so the mobile scroll container cannot clip it.
const tabLabelClasses =
  "mt-2 mb-1 rounded-lg px-2 py-1 whitespace-nowrap group-focus-visible:ring-2 group-focus-visible:ring-primary";

export interface PageNavMenuTheme {
  textColor: string;
  borderColor: string;
  activeTextColor: string;
  activeBorderColor: string;
  // Venue accent color - used as a subtle tint for dropdown hover/active
  // states. Falls back to `activeBorderColor` when not provided so existing
  // callers don't lose visual feedback.
  accentColor?: string;
}

interface PageNavMenuProps {
  links: PageNavLink[];
  theme?: PageNavMenuTheme;
  className?: string;
  onLinkClick?: (link: PageNavLink, event?: React.MouseEvent) => void;
  onViewMoreClick?: () => void;
  footerLink?: {
    label: string;
    href: string;
  };
  maxMobileLinks?: number;
  matchSubPaths?: boolean;
  horizontalSpacer?: boolean; // display a horizontal spacer element?
  /**
   * Optional override for the active link. When provided, pathname-based
   * detection is bypassed and this href is treated as active. Useful for
   * tabs driven by React state rather than the URL.
   */
  activeHref?: string | null;
}

const defaultTheme: PageNavMenuTheme = {
  textColor: "#787b7e", // secondary blue
  borderColor: "#C7C8C9",
  activeTextColor: "#29333c", // primary blue
  activeBorderColor: "#29333c", // primary blue
};

export default function PageNavMenu({
  links,
  theme = defaultTheme,
  className,
  onLinkClick,
  onViewMoreClick,
  footerLink,
  maxMobileLinks = 5,
  matchSubPaths = false,
  horizontalSpacer = false,
  activeHref,
}: PageNavMenuProps) {
  const isDesktop = useMediaQuery("(min-width: 768px)");
  const [isOpen, setIsOpen] = useState(false);
  const pathname = usePathname();

  const activeLinkClasses = "font-semibold text-[var(--nav-active-color)]";
  const commonLinkClasses = cn(
    "group relative flex flex-col justify-center md:items-center items-start px-3 bg-transparent transition-colors duration-200 ease-in-out leading-tight",
    "text-[var(--nav-color)] outline-none md:hover:text-[var(--nav-active-color)]"
  );

  const handleLinkClick = (link: PageNavLink, event?: React.MouseEvent) => {
    onLinkClick?.(link, event);
    setIsOpen(false);
  };

  const handleViewMoreClick = () => {
    onViewMoreClick?.();
    setIsOpen(true);
  };

  const visibleLinks = isDesktop ? links : links.slice(0, maxMobileLinks);
  const hasMoreLinks = !isDesktop && links.length > maxMobileLinks;

  if (isDesktop) {
    return (
      <div className="flex flex-row items-center justify-start w-full">
        <NavMenu
          links={links}
          pathname={pathname}
          commonLinkClasses={commonLinkClasses}
          activeLinkClasses={activeLinkClasses}
          onLinkClick={handleLinkClick}
          className={cn("w-full flex-wrap", className)}
          theme={theme}
          matchSubPaths={matchSubPaths}
          activeHrefOverride={activeHref}
        />
      </div>
    );
  }

  return (
    <>
      <div className="flex flex-row items-center justify-start w-full overflow-x-scroll hide-scrollbar">
        {/* add a "spacer" element to simulate padding inside the navigation menu, only on mobile */}
        {horizontalSpacer && <div className="w-4 flex-shrink-0" />}
        <NavMenu
          links={visibleLinks}
          pathname={pathname}
          commonLinkClasses={commonLinkClasses}
          activeLinkClasses={activeLinkClasses}
          onLinkClick={handleLinkClick}
          theme={theme}
          showViewMore={hasMoreLinks}
          className={className}
          onViewMoreClick={handleViewMoreClick}
          matchSubPaths={matchSubPaths}
          activeHrefOverride={activeHref}
        />
        {/* right-side "spacer" element, same as above */}
        {horizontalSpacer && <div className="w-4 flex-shrink-0" />}
      </div>
      <PageNavDrawerMenu
        isOpen={isOpen}
        onOpenChange={setIsOpen}
        links={links}
        pathname={pathname}
        onLinkClick={handleLinkClick}
        footerLink={footerLink}
        matchSubPaths={matchSubPaths}
        activeHrefOverride={activeHref}
      />
    </>
  );
}

interface NavMenuProps {
  links: PageNavLink[];
  pathname: string;
  commonLinkClasses: string;
  activeLinkClasses: string;
  onLinkClick: (link: PageNavLink, event?: React.MouseEvent) => void;
  className?: string;
  theme: PageNavMenuTheme;
  showViewMore?: boolean;
  onViewMoreClick?: () => void;
  matchSubPaths: boolean;
  activeHrefOverride?: string | null;
}

const SLIDER_TRANSITION = "transform 300ms ease, width 300ms ease";

const NavMenu = ({
  links,
  pathname,
  commonLinkClasses,
  activeLinkClasses,
  onLinkClick,
  className,
  theme: {
    textColor: color,
    borderColor,
    activeTextColor,
    activeBorderColor,
    accentColor,
  },
  showViewMore = false,
  onViewMoreClick,
  matchSubPaths,
  activeHrefOverride,
}: NavMenuProps) => {
  const navRef = useRef<HTMLElement>(null);
  const tabRefs = useRef<Map<string, HTMLElement>>(new Map());
  const [sliderStyle, setSliderStyle] = useState<{
    left: number;
    top: number;
    width: number;
  } | null>(null);
  // Row height drives a tiled background gradient that draws a full-width
  // underline at the bottom of every wrapped row (not just the last one).
  const [rowHeight, setRowHeight] = useState<number | null>(null);
  const hasAnimated = useRef(false);
  // Tracks which group's dropdown is currently open so we can suppress the
  // active-tab slider while the active group is being inspected.
  const [openGroupKey, setOpenGroupKey] = useState<string | null>(null);

  // Derive the active link's key (id or href) as a stable primitive. A group
  // is considered active when any of its children matches the current path.
  // The traversal is shared with PageNavDrawerMenu via findActiveNav so the two
  // surfaces can never disagree on what's active.
  const activeKey = useMemo(
    () =>
      findActiveNav(links, pathname, { matchSubPaths, activeHrefOverride })
        ?.key ?? null,
    [links, pathname, matchSubPaths, activeHrefOverride]
  );

  // Reads refs directly - no object dependencies, only primitives
  const measureActiveTab = useCallback(() => {
    const nav = navRef.current;
    if (!nav) return;

    // All tabs share the same height; measure any one to get row height.
    const firstTab = tabRefs.current.values().next().value;
    if (firstTab) {
      const h = firstTab.getBoundingClientRect().height;
      setRowHeight((prev) => (prev === h ? prev : h));
    }

    if (!activeKey) {
      setSliderStyle(null);
      return;
    }
    const tab = tabRefs.current.get(activeKey);
    if (!tab) return;
    const navRect = nav.getBoundingClientRect();
    const tabRect = tab.getBoundingClientRect();
    setSliderStyle({
      left: tabRect.left - navRect.left + nav.scrollLeft,
      // Sit the 2px slider directly on the active row's bottom border
      top: tabRect.bottom - navRect.top + nav.scrollTop - 2,
      width: tabRect.width,
    });
  }, [activeKey]);

  // Re-measure when labels (and therefore tab widths) change, even when
  // activeHref is unchanged - e.g. a label going from "Shows" to "Shows (13)".
  const labelsKey = useMemo(
    () => links.map((link) => link.label).join("|"),
    [links]
  );

  // Measure before paint when active tab, link set, or label widths change
  useLayoutEffect(() => {
    measureActiveTab();
    // Scroll active tab into view only when nav is overflowing (mobile)
    if (activeKey) {
      const nav = navRef.current;
      const el = tabRefs.current.get(activeKey);
      if (nav && el && nav.scrollWidth > nav.clientWidth) {
        el.scrollIntoView({
          behavior: "smooth",
          block: "nearest",
          inline: "nearest",
        });
      }
    }
  }, [measureActiveTab, labelsKey]);

  // Enable transition after first measurement so initial render has no slide-in
  useEffect(() => {
    if (sliderStyle) hasAnimated.current = true;
  }, [sliderStyle]);

  // Re-measure on container resize / font loading - stable, created once per activeHref
  useEffect(() => {
    const nav = navRef.current;
    if (!nav) return;
    const ro = new ResizeObserver(() => measureActiveTab());
    ro.observe(nav);
    return () => ro.disconnect();
  }, [measureActiveTab]);

  const navStyle: NavCSSProperties = {
    color,
    borderColor,
    "--nav-color": color,
    "--nav-active-color": activeTextColor,
    "--nav-border-color": borderColor,
    // Tiled gradient draws a 1px line at the bottom of each row, spanning the
    // full container width. Falls back to a single line at the nav's bottom
    // before row height is measured (e.g. SSR / first paint).
    backgroundImage: `linear-gradient(to bottom, transparent calc(100% - 1px), var(--nav-border-color) calc(100% - 1px))`,
    backgroundSize: rowHeight ? `100% ${rowHeight}px` : "100% 100%",
    backgroundRepeat: "repeat-y",
  };

  // Accent-tinted backgrounds for dropdown hover/active. We compute these
  // once per render and pass them through inline CSS variables on the
  // dropdown content so children can reference them via Tailwind arbitrary
  // values. Falls back to the active border color when no accent is set.
  const accent = accentColor ?? activeBorderColor;
  const dropdownVars: DropdownCSSProperties = useMemo(
    () => ({
      "--dropdown-accent": accent,
      "--dropdown-accent-hover-bg": withOpacity(accent, 0.08),
      "--dropdown-accent-active-bg": withOpacity(accent, 0.14),
    }),
    [accent]
  );

  return (
    <nav
      ref={navRef}
      aria-label="Section navigation"
      className={cn(
        "relative flex flex-row text-[16px] font-medium -mt-2",
        className
      )}
      style={navStyle}
    >
      {links.map((link) => {
        const key = linkKey(link);
        const isActive = activeKey === key;
        const isExternal = link.type === "external";
        const hasChildren = !!link.children && link.children.length > 0;

        if (hasChildren) {
          return (
            <DropdownMenu
              key={key}
              onOpenChange={(open) =>
                setOpenGroupKey((prev) =>
                  open ? key : prev === key ? null : prev
                )
              }
            >
              <DropdownMenuTrigger asChild>
                <button
                  ref={(el) => {
                    if (el) tabRefs.current.set(key, el);
                    else tabRefs.current.delete(key);
                  }}
                  type="button"
                  aria-current={isActive ? "page" : undefined}
                  // While the dropdown is open the trigger picks up the full
                  // active-style treatment - bold + active color - so it
                  // doesn't look half-applied during the interaction. The
                  // slider underline is suppressed separately below when the
                  // *active* group's dropdown is the one being inspected.
                  className={cn(
                    commonLinkClasses,
                    "data-[state=open]:font-semibold data-[state=open]:text-[var(--nav-active-color)]",
                    isActive && activeLinkClasses
                  )}
                >
                  <span
                    className={cn(
                      tabLabelClasses,
                      "flex flex-row items-center gap-x-1"
                    )}
                  >
                    {link.icon}
                    {link.label}
                    <ChevronDown className="w-4 h-4" />
                  </span>
                </button>
              </DropdownMenuTrigger>
              <DropdownMenuContent
                align="start"
                sideOffset={4}
                style={dropdownVars}
                className="p-0 border-0 text-[16px] font-medium"
              >
                {(link.children ?? []).map((child) => {
                  const childIsExternal = child.type === "external";
                  const childIsActive = matchesPath(
                    child.href,
                    pathname,
                    matchSubPaths
                  );
                  return (
                    <DropdownMenuItem
                      key={linkKey(child)}
                      asChild
                      aria-current={childIsActive ? "page" : undefined}
                      // Items render edge-to-edge (no container padding) and
                      // pick up an accent-tinted background on hover / active.
                      // A 3px reserved left border keeps text aligned and is
                      // colored with the venue accent on the active row.
                      style={
                        childIsActive
                          ? {
                              backgroundColor:
                                "var(--dropdown-accent-active-bg)",
                              borderLeftColor: "var(--dropdown-accent)",
                            }
                          : undefined
                      }
                      className={cn(
                        "cursor-pointer text-[16px] font-medium pl-4 pr-10 py-2 rounded-none text-gray-800 border-l-[3px] border-l-transparent focus:bg-[var(--dropdown-accent-hover-bg)] focus:text-gray-900",
                        childIsActive && "text-gray-900 font-semibold"
                      )}
                    >
                      <Link
                        href={child.href}
                        target={childIsExternal ? "_blank" : undefined}
                        rel={
                          childIsExternal ? "noopener noreferrer" : undefined
                        }
                        onClick={(e) => onLinkClick(child, e)}
                        className="flex items-center gap-x-1"
                      >
                        {child.label}
                        {childIsExternal && (
                          <ArrowUpRight className="w-4 h-4" />
                        )}
                      </Link>
                    </DropdownMenuItem>
                  );
                })}
              </DropdownMenuContent>
            </DropdownMenu>
          );
        }

        return (
          <Link
            key={key}
            ref={(el) => {
              if (el) tabRefs.current.set(key, el);
              else tabRefs.current.delete(key);
            }}
            aria-current={isActive ? "page" : undefined}
            className={cn(commonLinkClasses, isActive && activeLinkClasses)}
            onClick={(e) => onLinkClick(link, e)}
            href={link.href}
            target={isExternal ? "_blank" : undefined}
            rel={isExternal ? "noopener noreferrer" : undefined}
          >
            <span
              className={cn(
                tabLabelClasses,
                link.icon && "flex flex-row items-center gap-x-1.5",
                isExternal && "flex flex-row items-center gap-x-0.5"
              )}
            >
              {link.icon}
              {link.label}
              {isExternal && <ArrowUpRight className="w-4 h-4" />}
            </span>
          </Link>
        );
      })}
      {showViewMore && (
        <button className={cn(commonLinkClasses)} onClick={onViewMoreClick}>
          <span className={tabLabelClasses}>View More</span>
        </button>
      )}
      {sliderStyle && openGroupKey !== activeKey && (
        <div
          aria-hidden
          className="absolute left-0 top-0 h-[2px] z-[1]"
          style={{
            backgroundColor: activeBorderColor,
            width: sliderStyle.width,
            transform: `translate(${sliderStyle.left}px, ${sliderStyle.top}px)`,
            transition: hasAnimated.current ? SLIDER_TRANSITION : "none",
          }}
        />
      )}
    </nav>
  );
};
